import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface Customer {
  // w-ums serialises customers through CustomerResponse, which is snake_case
  // and exposes the primary key as `id`. This interface still described the
  // raw gorm model (ID/CreatedAt/UpdatedAt), so every `c.ID` read undefined -
  // and the delete button posted to /api/v1/customers/undefined.
  id: number;
  created_at?: string;
  updated_at?: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile_number: string;
  date_of_birth: string;
  age: number;
  gender: string;
  address: string;
  role: string;
  notification_preferences: string;
  profile_picture: string;
}

interface CustomersState {
  customers: Customer[];
  selectedCustomer: Customer | null;
  loading: boolean;
  error: string | null;
}

const initialState: CustomersState = {
  customers: [],
  selectedCustomer: null,
  loading: false,
  error: null,
};

export const fetchCustomers = createAsyncThunk(
  'customers/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.ums}/api/v1/customers`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch customers');
    }
  }
);

export const fetchCustomerById = createAsyncThunk(
  'customers/fetchById',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.ums}/api/v1/customers/${id}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch customer');
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/delete',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.ums}/api/v1/customers/${id}`);
      return id;
    } catch (error: any) {
      // Prefer the server's message: it says what actually went wrong, where
      // axios' own only says the request failed.
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to delete customer'
      );
    }
  }
);

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearSelectedCustomer: (state) => {
      state.selectedCustomer = null;
    },
    clearCustomersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchCustomers.fulfilled, (state, action) => { state.loading = false; state.customers = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchCustomers.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(fetchCustomerById.pending, (state) => { state.loading = true; })
      .addCase(fetchCustomerById.fulfilled, (state, action) => { state.loading = false; state.selectedCustomer = action.payload?.data || action.payload; })
      .addCase(fetchCustomerById.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(deleteCustomer.fulfilled, (state, action) => { state.customers = state.customers.filter(c => c.id !== action.payload); });
  },
});

export const { clearSelectedCustomer, clearCustomersError } = customersSlice.actions;
export default customersSlice.reducer;
